"use client";

/***
 *
 * @file components/ColorPalette/index.tsx
 * @description ColorPalette component will be used for rendering the content.
 * @author Ayesha Kumar
 * @organization Truminds Software System
 * @since 23 May 2025
 *
 */

import React, { useEffect, useState } from "react";
// import "./ColorPalette.scss";
import Button from "../Button";
import classNames from "classnames";
import ColorBlock from "./ColorBlock";
import { getRandomHexColor } from "@/helpers/utils.helper";
import { ButtonWrapper } from "../ButtonWrapper";

type Props = {
  noOfSlots: Number;
  enableVerticalStack?: Boolean;
  btn_variant?: string;
  btn_size?: "m" | "l" | "s" | "xl";
  btn_label?: string;
  btn_hide?: boolean;
  btn_position?: "center" | "start" | "end";
};

const ColorPalette = ({ noOfSlots, enableVerticalStack, ...props }: Props) => {
  const [colorPalette, setColorPalette] = useState<string[]>(
    Array(Number(noOfSlots))
      ?.fill("")
      .map((c: string) => {
        c = getRandomHexColor();
        return c;
      })
  );

  useEffect(() => {
    setColorPalette(() =>
      Array(Number(noOfSlots))
        ?.fill("")
        .map((c: string) => {
          c = getRandomHexColor();
          return c;
        })
    );
  }, [noOfSlots]);

  const changeColorPalette = (colorArray: string[]) => {
    setColorPalette(
      colorArray?.map((c: string) => {
        c = getRandomHexColor();
        return c;
      })
    );
  };

  const textColor = (hexColor: string) => {
    hexColor = hexColor.replace("#", "");

    if (hexColor.length === 3) {
      hexColor = hexColor
        .split("")
        .map((ch) => ch + ch)
        .join("");
    }

    const r = parseInt(hexColor.substring(0, 2), 16);
    const g = parseInt(hexColor.substring(2, 4), 16);
    const b = parseInt(hexColor.substring(4, 6), 16);

    // Use relative luminance formula
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    return brightness > 128 ? "black" : "white";
  };

  const classNameForRadius = ({
    index,
    len,
  }: {
    index: number;
    len: number;
  }) => {
    if (len === 1) {
      return enableVerticalStack ? "rounded-[8px]" : "rounded-[10px]";
    } else if (index === 0) {
      return enableVerticalStack
        ? "rounded-tl-[8px] rounded-tr-[8px] rounded-br-[0] rounded-bl-[0]"
        : "rounded-tl-[10px] rounded-tr-[0] rounded-br-[0] rounded-bl-[10px]";
    } else if (index === len - 1) {
      return enableVerticalStack
        ? "rounded-tl-[0] rounded-tr-[0] rounded-br-[8px] rounded-bl-[8px]"
        : "rounded-tl-[0] rounded-tr-[10px] rounded-br-[10px] rounded-bl-[0]";
    }
    return "";
  };

  return (
    <div
      className={classNames(
        "ColorPalette",
        `flex flex-col items-${props?.btn_position}`
      )}
      data-testid="ColorPalette"
    >
      <div
        className={classNames(
          "flex w-[100%] items-stretch content-stretch rounded-[16px]",
          !props?.btn_hide && "mb-[30px]",
          enableVerticalStack && "flex-col"
        )}
      >
        {colorPalette?.map((crrColor: string, i) => {
          return (
            <ColorBlock
              key={crrColor}
              className={classNames(
                "growColorBlock",
                classNameForRadius({ index: i, len: colorPalette?.length })
              )}
              textColor={textColor(crrColor)}
              crrColor={crrColor}
            />
          );
        })}
      </div>

      {!props?.btn_hide && (
        <div>
          <ButtonWrapper
            id="cta-details"
            variant={props?.btn_variant}
            size={props?.btn_size}
            action={() => {
              changeColorPalette(colorPalette);
            }}
            className="flex gap-0.5 items-center"
            value={<>{props?.btn_label}</>}
            GTMEnabled={true}
            GTMPayload={{
              label: props?.btn_label,
              location: "/colorPalette",
              action: "changeColorPalette"
            }}
          />
        </div>
      )}
    </div>
  );
};

export default ColorPalette;
